import React, { useCallback, useEffect, useState } from 'react';
import { Drawer, Segmented, Tabs } from 'antd';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import NdoeImage from './NdoeImage';
import NdoeStatusBadge from './NdoeStatusBadge';
import { NdoeStatCard, NdoeStats } from './NdoeStatCard';
import { NdoeError, NdoeLoading } from './NdoeStates';
import './ndoeFicheArticle.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

const money = (v) => (v === null || v === undefined ? '—'
  : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} $`);
// Les montants étaient en français (« 62,55 $ ») et les pourcentages en anglais
// (« 40.4 % ») dans la même phrase. Un seul séparateur décimal, le nôtre.
const pct = (v, signe = false) => (v === null || v === undefined ? '—'
  : `${v > 0 && signe ? '+' : ''}${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`);
// En dessous de ce seuil, une baisse de prix d'achat est du bruit — un arrondi de
// facture, un frais de transport en moins. On ne l'appelle pas « promotion ».
const SEUIL_PROMO_PCT = -5;
// Libellés d'horizon : « 1 an » se lit, « 365 j » se calcule.
const LIB_HORIZON = { 7: '7 j', 30: '30 j', 90: '90 j', 365: '1 an' };
// La PÉRIODE PRÉCÉDENTE se nomme autrement : « les 1 an précédents » ne se dit pas.
const PERIODE_AVANT = {
  7: "la semaine d'avant", 30: "le mois d'avant",
  90: "les 3 mois d'avant", 365: "l'année d'avant",
};
const dateTxt = (v) => (v ? new Date(v).toLocaleDateString('fr-FR') : '—');

const MVT_LIBELLES = {
  4: 'Vente', 5: 'Retour client', 6: 'Échange (entrée)', 7: 'Échange (sortie)',
  10: 'Réception', 11: 'Avarie', 12: 'Départ livraison', 13: 'Retour livraison',
  14: 'Ajustement +', 15: 'Ajustement −',
};

const CONFIANCE_COUT = {
  reel: 'coût réel', rattrapage: 'rattrapage', moyenne: 'moyenne produit', estime: 'estimé',
};

// D'où vient le coût d'un arrivage. Le dire change la confiance qu'on accorde au
// chiffre : « saisi » est une facture, « rattrapage » une reconstitution.
const SOURCE_COUT = {
  saisi: 'saisi', reparti: 'réparti sur le lot', rattrapage: 'reconstitué', estime: 'estimé',
};

/**
 * Fiche article transverse : la carte d'identité d'un modèle, ouverte d'un clic
 * sur son intitulé depuis n'importe quel écran. Aucune donnée recalculée —
 * l'API assemble les briques vérifiées (vérité stock, ventes V4, coût, mouvements).
 *
 * Usage recommandé :
 *   const { openFiche, ficheElement } = useFicheArticle();
 *   …
 *   <NdoeArticleLink onClick={() => openFiche(r.id_produit, r.id_varianteProduit)}>{r.nom_produit}</NdoeArticleLink>
 *   {ficheElement}
 */
export default function NdoeFicheArticle({ open, idProduit, varianteId = null, onClose }) {
  const navigate = useNavigate();
  const [fiche, setFiche] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // HORIZON RÉGLABLE (31/07/2026) — demande du propriétaire : « au lieu d'avoir
  // des infos sur les 30 derniers jours uniquement, la possibilité de changer
  // l'horizon sur la fiche à 7 j, 90 j, 1 an ».
  //
  // Trente jours répond à « comment ça va en ce moment » et à rien d'autre.
  // Sept jours dit si une promo prend ; un an révèle une saisonnalité qu'aucune
  // fenêtre courte ne montre. La question change, la fenêtre doit suivre.
  const [horizon, setHorizon] = useState(30);
  const [onglet, setOnglet] = useState('apercu');

  const load = useCallback(async () => {
    if (!idProduit) return;
    setLoading(true);
    setFiche(null);
    try {
      const { data } = await axios.get(`${DOMAIN}/api/produit/fiche/${idProduit}?jours=${horizon}`);
      setFiche(data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Fiche indisponible.');
    } finally { setLoading(false); }
  }, [idProduit, horizon]);

  useEffect(() => { if (open) load(); }, [open, load]);
  // Chaque fiche s'ouvre sur 30 jours ET sur l'Aperçu : l'horizon comme l'onglet
  // sont des questions qu'on se pose POUR un article, pas des réglages qu'on
  // traîne d'un article à l'autre.
  useEffect(() => { if (open) { setHorizon(30); setOnglet('apercu'); } }, [open, idProduit]);

  const p = fiche?.produit;
  const cible = fiche?.variantes?.find((v) => Number(v.id_varianteProduit) === Number(varianteId));
  const img = cible?.img || fiche?.variantes?.find((v) => v.img)?.img;
  const libHorizon = LIB_HORIZON[horizon] || `${horizon} j`;

  // ── Le contenu des quatre onglets ─────────────────────────────────────────
  //
  // La fiche disait tout d'un coup, dans un tiroir de 560 px : sur un téléphone,
  // la marge et les mouvements étaient à trois écrans de défilement du prix.
  // AUCUN chiffre ne change ici — on range.

  const ongletApercu = fiche && (
    <>
      <NdoeStats>
        <NdoeStatCard label="Disponible" value={fiche.totaux.disponible} gold />
        <NdoeStatCard label="Stock comptable" value={fiche.totaux.stock} />
        <NdoeStatCard label={`Vendu ${libHorizon}`} value={fiche.totaux.vendu_horizon ?? fiche.totaux.vendu_30j} />
        <NdoeStatCard label="Vendu au total" value={fiche.totaux.vendu_total} />
        {fiche.totaux.en_circulation > 0 && <NdoeStatCard label="En course (livreur)" value={fiche.totaux.en_circulation} />}
        {fiche.totaux.reservees > 0 && <NdoeStatCard label="Réservées" value={fiche.totaux.reservees} />}
        {fiche.totaux.avariees > 0 && <NdoeStatCard label="Avariées (à part)" value={fiche.totaux.avariees} />}
      </NdoeStats>

      {/* TENDANCE ET CLASSEMENT — deux questions, deux réponses.
          La tendance compare la paire À ELLE-MÊME ; le rang la compare AUX
          AUTRES. Un chiffre de ventes seul ne dit ni l'un ni l'autre :
          8 paires est excellent pour une niche, médiocre pour un basique. */}
      {fiche.tendance && (
        <div className="ndoe-fiche__tendance">
          <div className={`ndoe-fiche__tend ${fiche.tendance.sens}`}>
            <span className="ndoe-fiche__tend-fleche">
              {fiche.tendance.sens === 'hausse' ? '▲' : fiche.tendance.sens === 'baisse' ? '▼' : '='}
            </span>
            <span>
              {fiche.tendance.variation_pct === null
                ? (fiche.tendance.vendu > 0
                  ? <>Rien de vendu sur {PERIODE_AVANT[horizon] || `les ${horizon} jours d'avant`} — <b>tout est récent</b></>
                  : <>Aucune vente, ni maintenant ni avant</>)
                : <>
                    <b>{pct(fiche.tendance.variation_pct, true)}</b>
                    {' '}vs la période précédente ({fiche.tendance.vendu} contre {fiche.tendance.vendu_precedent})
                  </>}
            </span>
          </div>
          <div className="ndoe-fiche__rang">
            {fiche.tendance.rang
              ? <>
                  <b>{fiche.tendance.rang}<sup>{fiche.tendance.rang === 1 ? 'er' : 'e'}</sup></b>
                  {' '}des {fiche.tendance.modeles_vendeurs} modèles qui ont vendu
                  {' '}<small>({fiche.tendance.modeles_total} au catalogue)</small>
                  {' · '}<span className={`ndoe-fiche__pos p-${fiche.tendance.position.replace(/[^a-z]/g, '')}`}>{fiche.tendance.position}</span>
                </>
              : <span className="ndoe-fiche__pos p-aucune">Aucune vente sur cette période — pas de classement</span>}
          </div>
        </div>
      )}

      <p className="ndoe-fiche__note">
        Dernière vente : <b>{dateTxt(fiche.derniere_vente)}</b>
        {' · '}au catalogue depuis le <b>{dateTxt(p.date_entrant)}</b>
      </p>
    </>
  );

  const ongletStock = fiche && (
    <>
      <div className="ndoe-fiche__pt">
        <div className="ndoe-fiche__pt-row ndoe-fiche__pt-row--head">
          <span>P.</span><span>Dispo</span><span>Stock</span><span>Rés.</span><span>Course</span><span>Avar.</span><span>Vendu {libHorizon}</span>
        </div>
        {fiche.variantes.map((v) => (
          <div
            key={v.id_varianteProduit}
            className={`ndoe-fiche__pt-row ${Number(v.id_varianteProduit) === Number(varianteId) ? 'is-cible' : ''}`}
          >
            <span><b>P{v.taille ?? '—'}</b>{v.couleur ? <small> {v.couleur}</small> : null}</span>
            <span className="num"><b>{v.disponible}</b></span>
            <span className="num">{v.physique}</span>
            <span className="num">{Number(v.reservee || 0) + Number(v.reserve_commerciale || 0) || '—'}</span>
            <span className="num">{v.en_circulation || '—'}</span>
            <span className="num">{v.avariees || '—'}</span>
            <span className="num">{(v.vendu_horizon ?? v.vendu_30j) || '—'}</span>
          </div>
        ))}
      </div>
      {!fiche.variantes.length && <p className="ndoe-fiche__vide">Aucune variante enregistrée.</p>}
    </>
  );

  // ── Tarifs et marges ──────────────────────────────────────────────────────
  //
  // « Le prix d'achat d'un modèle ou d'une variante peut changer, par exemple
  // lorsque j'ai une promotion chez mon fournisseur — comme c'est d'ailleurs
  // souvent le cas. » (propriétaire, 08/2026)
  //
  // Mesuré : 30 des 126 modèles réceptionnés ont connu PLUSIEURS coûts d'achat.
  // Le coût unique — une moyenne — cachait pour eux le prix réellement payé, et
  // donc la marge réellement faite sur chaque arrivage.
  const histo = fiche?.historique_couts || [];
  const varia = fiche?.variation_cout;
  const ongletTarifs = fiche && (
    <>
      <div className="ndoe-fiche__tarif-tete">
        <div>
          <span className="ndoe-fiche__tarif-l">Prix de vente</span>
          <b className="num ndoe-fiche__tarif-v">{money(p.prix)}</b>
        </div>
        <div>
          <span className="ndoe-fiche__tarif-l">Coût unitaire retenu</span>
          <b className="num ndoe-fiche__tarif-v">{fiche.cout ? money(fiche.cout.cout_unitaire) : '—'}</b>
          {fiche.cout && <small> {CONFIANCE_COUT[fiche.cout.confiance] || fiche.cout.confiance}</small>}
        </div>
        <div>
          <span className="ndoe-fiche__tarif-l">Marge</span>
          {fiche.cout && fiche.cout.marge_unitaire !== null
            ? <b className={`num ndoe-fiche__tarif-v ${fiche.cout.marge_unitaire < 0 ? 'is-neg' : ''}`}>
                {money(fiche.cout.marge_unitaire)} <small>({pct(fiche.cout.taux_marge)})</small>
              </b>
            : <b className="num ndoe-fiche__tarif-v">—</b>}
        </div>
      </div>

      {!fiche.cout && (
        <p className="ndoe-fiche__cout ndoe-fiche__cout--absent">
          Aucun coût d'achat connu — la marge n'est pas calculée (jamais estimée à zéro).
        </p>
      )}

      {/* L'alerte qui compte : le coût a bougé au dernier arrivage. */}
      {varia && varia.ecart_pct !== null && varia.ecart_pct !== 0 && (
        <p className={`ndoe-fiche__varia ${varia.ecart_pct < 0 ? 'baisse' : 'hausse'}`}>
          <span className="ndoe-fiche__tend-fleche">{varia.ecart_pct < 0 ? '▼' : '▲'}</span>
          Le prix d'achat est passé de <b>{money(varia.precedent)}</b> à <b>{money(varia.dernier)}</b>
          {' '}au dernier arrivage <b>({pct(varia.ecart_pct, true)})</b>.
          {varia.ecart_pct <= SEUIL_PROMO_PCT && ' Une promotion fournisseur, sans doute.'}
        </p>
      )}

      <h4 className="ndoe-fiche__titre">Prix d'achat, arrivage par arrivage</h4>
      {histo.length ? (
        <div className="ndoe-fiche__histo">
          <div className="ndoe-fiche__histo-row ndoe-fiche__histo-row--head">
            <span>Arrivage</span><span>Reçu</span><span>Achat / u</span><span>Frais / u</span><span>Marge / u</span>
          </div>
          {histo.map((h) => (
            <div key={h.cle} className="ndoe-fiche__histo-row">
              <span>
                {dateTxt(h.date_arrivage)}
                {h.sources && <small className="ndoe-fiche__histo-src"> {SOURCE_COUT[h.sources] || h.sources}</small>}
              </span>
              <span className="num">
                {h.quantite}
                {h.quantite_sans_cout > 0 && (
                  <small className="ndoe-fiche__histo-src" title="paires reçues sans coût saisi">
                    {' '}dont {h.quantite_sans_cout} sans coût
                  </small>
                )}
              </span>
              <span className="num"><b>{money(h.cout_marchandise_u)}</b></span>
              <span className="num">{h.cout_frais_u ? money(h.cout_frais_u) : '—'}</span>
              <span className={`num ${h.marge_u !== null && h.marge_u < 0 ? 'is-neg' : ''}`}>
                {h.marge_u === null ? '—' : <>{money(h.marge_u)} <small>({pct(h.taux_marge)})</small></>}
              </span>
            </div>
          ))}
        </div>
      ) : <p className="ndoe-fiche__vide">Aucun arrivage tracé pour ce modèle.</p>}

      {histo.length > 0 && (
        <p className="ndoe-fiche__note ndoe-fiche__note--discret">
          La marge est calculée au prix de vente <b>d'aujourd'hui</b> ({money(p.prix)}) :
          le prix pratiqué à l'époque de chaque arrivage n'est pas conservé.
        </p>
      )}
    </>
  );

  const ongletHistorique = fiche && (
    <>
      {fiche.mouvements.length ? (
        <ul className="ndoe-fiche__mvts">
          {fiche.mouvements.map((m) => (
            <li key={m.id_mouvement}>
              <span className="ndoe-fiche__mvt-type">{MVT_LIBELLES[m.id_type_mouvement] || `Type ${m.id_type_mouvement}`}</span>
              <span className="num">{m.quantite}{m.taille ? ` · P${m.taille}` : ''}</span>
              <small>{dateTxt(m.date_mouvement)}</small>
            </li>
          ))}
        </ul>
      ) : <p className="ndoe-fiche__vide">Aucun mouvement tracé.</p>}
      <p className="ndoe-fiche__note">
        Dernière vente : <b>{dateTxt(fiche.derniere_vente)}</b> · vendu total : <b className="num">{fiche.totaux.vendu_total}</b>
      </p>
    </>
  );

  return (
    <Drawer open={open} onClose={onClose} width={560} className="ndoe-fiche"
      title={p ? p.nom_produit : 'Fiche article'}>
      {error && !loading && <NdoeError text={error} onRetry={load} />}
      {loading && <NdoeLoading rows={5} />}
      {!loading && !error && fiche && (
        <div className="ndoe-fiche__corps">
          <div className="ndoe-fiche__head">
            <NdoeImage src={img} alt={p.nom_produit} size="hero" />
            <div className="ndoe-fiche__meta">
              <b>{[p.marque, p.categorie].filter(Boolean).join(' · ') || '—'}</b>
              <small>{p.matiere || 'Matière non renseignée'}</small>
              <small>Réf. {p.code_produit || '—'} · au catalogue depuis le {dateTxt(p.date_entrant)}</small>
              <div className="ndoe-fiche__prix num">{money(p.prix)}</div>
              {Number(p.deconseille) === 1 && (
                <NdoeStatusBadge kind="custom" tone="danger" label="Déconseillé" />
              )}
            </div>
          </div>

          {/* L'horizon d'analyse. Placé AVANT les chiffres qu'il gouverne, et
              HORS des onglets parce qu'il en gouverne deux : l'Aperçu et la
              colonne « vendu » des pointures. */}
          <div className="ndoe-fiche__horizon">
            <span className="ndoe-fiche__horizon-l">Analyser sur</span>
            <Segmented
              size="small" value={horizon} onChange={setHorizon}
              options={[
                { value: 7, label: '7 j' },
                { value: 30, label: '30 j' },
                { value: 90, label: '90 j' },
                { value: 365, label: '1 an' },
              ]}
            />
          </div>

          <Tabs
            className="ndoe-fiche__onglets"
            activeKey={onglet}
            onChange={setOnglet}
            items={[
              { key: 'apercu', label: 'Aperçu', children: ongletApercu },
              { key: 'stock', label: `Variantes (${fiche.variantes.length})`, children: ongletStock },
              { key: 'tarifs', label: 'Tarifs et marges', children: ongletTarifs },
              { key: 'histo', label: 'Historique', children: ongletHistorique },
            ]}
          />

          <div className="ndoe-fiche__actions">
            <button type="button" className="ndoe-fiche__lien" onClick={() => { onClose(); navigate('/varianteProduit'); }}>
              Ouvrir dans Variantes produit →
            </button>
            <button type="button" className="ndoe-fiche__lien" onClick={() => { onClose(); navigate('/rapports/performance'); }}>
              Voir dans Performance produits →
            </button>
          </div>
        </div>
      )}
    </Drawer>
  );
}

// Libellé d'article cliquable — s'insère dans n'importe quelle cellule/carte sans
// déclencher le clic de la ligne parente.
export function NdoeArticleLink({ onClick, children, className = '' }) {
  return (
    <button
      type="button"
      className={`ndoe-article-link ${className}`}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title="Voir la fiche article"
    >
      {children}
    </button>
  );
}

// Hook d'intégration : un état + un élément à rendre, deux lignes par écran.
export function useFicheArticle() {
  const [cible, setCible] = useState(null);
  const openFiche = useCallback((idProduit, varianteId = null) => setCible({ idProduit, varianteId }), []);
  const ficheElement = (
    <NdoeFicheArticle
      open={!!cible}
      idProduit={cible?.idProduit}
      varianteId={cible?.varianteId}
      onClose={() => setCible(null)}
    />
  );
  return { openFiche, ficheElement };
}
